import { createBrowserRouter, Outlet } from 'react-router'
import { lazy, Suspense, type ReactNode } from 'react'
import { AuthProvider, registerSprintRoutes } from '@waago/api'
import { MobileFrame } from '@waago/ui'
import { AppTabs } from '../components/AppTabs'
import { SprintGate } from '../components/SprintGate'
import { CLIENT_SPRINTS } from './sprints'

// Installed before the router renders, so the first navigation already knows
// what this build is showing.
registerSprintRoutes(CLIENT_SPRINTS)
import { RequireAuth } from './RequireAuth'
import LoginPage from './pages/LoginPage'
import WelcomePage from './pages/WelcomePage'

// Route-level splitting keeps the initial bundle inside the mobile budget.
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const SignupWizard = lazy(() => import('./pages/SignupWizard'))
const SignupSuccessPage = lazy(() => import('./pages/SignupSuccessPage'))
const SearchPage = lazy(() => import('./pages/SearchPage'))
const ProviderPage = lazy(() => import('./pages/ProviderPage'))
const MissionPage = lazy(() => import('./pages/MissionPage'))
const PaymentPage = lazy(() => import('./pages/PaymentPage'))
const PaymentDonePage = lazy(() => import('./pages/PaymentDonePage'))
const HomePage = lazy(() => import('./pages/HomePage'))
const RequestPage = lazy(() => import('./pages/RequestPage'))
const ActivityPage = lazy(() => import('./pages/ActivityPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const AccountPage = lazy(() => import('./pages/AccountPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const PaymentMethodsPage = lazy(() => import('./pages/PaymentMethodsPage'))
const MembersPage = lazy(() => import('./pages/MembersPage'))
const AddPaymentMethodPage = lazy(() => import('./pages/AddPaymentMethodPage'))
const SecurityPage = lazy(() => import('./pages/SecurityPage'))
const PhoneChangePage = lazy(() => import('./pages/PhoneChangePage'))
const LanguagePage = lazy(() => import('./pages/LanguagePage'))
const PermissionsPage = lazy(() => import('./pages/PermissionsPage'))
const ReportPage = lazy(() => import('./pages/ReportPage'))
const ReportDetailPage = lazy(() => import('./pages/ReportDetailPage'))
const LegalPage = lazy(() => import('./pages/LegalPage'))
const FiltrePage = lazy(() => import('./pages/FiltrePage'))
const AidePage = lazy(() => import('./pages/AidePage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const RequestPostedPage = lazy(() => import('./pages/RequestPostedPage'))
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'))

function Loading() {
  return (
    <p className="animate-slide-up p-8 text-center text-sm text-slate-500" role="status">
      Chargement…
    </p>
  )
}

/**
 * The one place the session lives.
 *
 * <p>Every route branch used to mount its own `<AuthProvider>`, which meant
 * navigating from `/connexion` to `/` unmounted one and mounted another — and
 * the new one immediately exchanged the refresh cookie again, moments after the
 * login had just issued it. Under React's development double-mount that was two
 * rotations of the same token, which is indistinguishable from a stolen one, so
 * the server revoked the family and signed the user straight back out.
 *
 * <p>Mounted once above the router's outlet, the session is restored once per
 * page load and survives every navigation.
 */
function Root() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  )
}

function Shell() {
  return (
    <RequireAuth>
      <Suspense fallback={<Loading />}>
        <AppTabs />
      </Suspense>
    </RequireAuth>
  )
}

/**
 * A signed-in screen with no tab bar.
 *
 * Search and the provider profile are full-screen in the design — search has a
 * back arrow, the profile has a close X — and both are places you leave rather
 * than navigate away from. Keeping the tab bar under them would offer a second
 * way out that the design does not have, and would eat 62px of a screen that
 * already scrolls.
 */
function Overlay({ children }: { children: ReactNode }) {
  return (
    <RequireAuth>
      <MobileFrame fill>
        <main id="main" className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <SprintGate>
            <Suspense fallback={<Loading />}>{children}</Suspense>
          </SprintGate>
        </main>
      </MobileFrame>
    </RequireAuth>
  )
}

/** The screens reachable before sign-in. The context comes from {@link Root}. */
function Public({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      {
        path: '/bienvenue',
        element: (
          <Public>
            <WelcomePage />
          </Public>
        ),
      },
      {
        path: '/connexion',
        element: (
          <Public>
            <LoginPage />
          </Public>
        ),
      },
      {
        path: '/inscription',
        element: (
          <Public>
            <RegisterPage />
          </Public>
        ),
      },
      {
        path: '/inscription/type',
        element: (
          <Public>
            <SignupWizard />
          </Public>
        ),
      },
      {
        // Reached with a session already issued, so it is not behind RequireAuth:
        // a failed activation must still land somewhere that explains itself.
        path: '/bienvenue-compte',
        element: (
          <Public>
            <SignupSuccessPage />
          </Public>
        ),
      },
      {
        path: '/',
        element: <Shell />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'demande/:activity', element: <RequestPage /> },
          { path: 'activite', element: <ActivityPage /> },
          { path: 'notifications', element: <NotificationsPage /> },
          { path: 'compte', element: <AccountPage /> },

          { path: 'aide', element: <AidePage /> },
        ],
      },
      {
        // The "posté" confirmation, before the detail screen (frames
        // `5034:18110`, `5034:19306`).
        path: '/demandes/:id/postee',
        element: (
          <Overlay>
            <RequestPostedPage />
          </Overlay>
        ),
      },
      {
        // Frame `5099:3829` puts Messagerie beside Détails on the mission
        // screen, so this path opens that tab rather than a screen of its own.
        // It stays a route because it is the link every "nouveau message"
        // notification carries.
        path: '/demandes/:id/messages',
        element: (
          <Overlay>
            <MissionPage />
          </Overlay>
        ),
      },
      {
        path: '/aide/contact',
        element: (
          <Overlay>
            <ContactPage />
          </Overlay>
        ),
      },
      {
        path: '/demandes/filtre',
        element: (
          <Overlay>
            <FiltrePage />
          </Overlay>
        ),
      },
      {
        path: '/recherche',
        element: (
          <Overlay>
            <SearchPage />
          </Overlay>
        ),
      },
      {
        // "Avis" — every review, on its own screen (frame `5134:22248`).
        path: '/prestataire/:id/avis',
        element: (
          <Overlay>
            <ReviewsPage />
          </Overlay>
        ),
      },
      {
        path: '/prestataire/:id',
        element: (
          <Overlay>
            <ProviderPage />
          </Overlay>
        ),
      },
      {
        path: '/demandes/:id',
        element: (
          <Overlay>
            <MissionPage />
          </Overlay>
        ),
      },
      {
        path: '/demandes/:id/paiement',
        element: (
          <Overlay>
            <PaymentPage />
          </Overlay>
        ),
      },
      {
        path: '/demandes/:id/paiement/confirme',
        element: (
          <Overlay>
            <PaymentDonePage />
          </Overlay>
        ),
      },
      {
        // Filing one. `?prestataire=` names who it is about when it is raised from
        // a provider profile; without it the report is about the platform.
        path: '/signalement',
        element: (
          <Overlay>
            <ReportPage />
          </Overlay>
        ),
      },
      {
        // The same form, already attached to a mission.
        path: '/demandes/:id/signalement',
        element: (
          <Overlay>
            <ReportPage />
          </Overlay>
        ),
      },
      {
        path: '/signalement/:id',
        element: (
          <Overlay>
            <ReportDetailPage />
          </Overlay>
        ),
      },
      {
        path: '/compte/utilisateurs',
        element: (
          <Overlay>
            <MembersPage />
          </Overlay>
        ),
      },
      {
        path: '/compte/paiement',
        element: (
          <Overlay>
            <PaymentMethodsPage />
          </Overlay>
        ),
      },
      {
        path: '/compte/paiement/ajouter',
        element: (
          <Overlay>
            <AddPaymentMethodPage />
          </Overlay>
        ),
      },
      {
        path: '/compte/profil',
        element: (
          <Overlay>
            <ProfilePage />
          </Overlay>
        ),
      },
      {
        path: '/compte/securite',
        element: (
          <Overlay>
            <SecurityPage />
          </Overlay>
        ),
      },
      {
        path: '/compte/securite/numero',
        element: (
          <Overlay>
            <PhoneChangePage />
          </Overlay>
        ),
      },
      {
        path: '/compte/langue',
        element: (
          <Overlay>
            <LanguagePage />
          </Overlay>
        ),
      },
      {
        path: '/compte/permission',
        element: (
          <Overlay>
            <PermissionsPage />
          </Overlay>
        ),
      },
      {
        path: '/mentions-legales',
        element: (
          <Overlay>
            <LegalPage />
          </Overlay>
        ),
      },
    ],
  },
])
